import { RolodexIndication } from 'features/indicationTriage'
import { GeckoMarkupLayer, RolodexEntryJson, RolodexEntryType } from 'types'

const CLASSIFICATION_COLORS: Record<string, string> = {
  pass: '#52c41a',
  fail: '#ff4d4f',
  derate: '#faad14',
  pending: '#1890ff',
}
const UNCLASSIFIED_COLOR = '#bfbfbf'
const SELECTED_HIGHLIGHT_COLOR = '#000000'
const TITLE_PRIMITIVE_PREFIX = '@@title'

/**
 * Prefix applied to every markup-layer id this module emits. Use the helper
 * below rather than reading the constant directly so the test stays in lockstep
 * with what the viz pane checks for.
 */
export const FFS_INDICATION_LAYER_PREFIX = 'ffs-triage-indication_'

/** True iff `id` is a markup-layer id produced by `indicationToRectLayer`. */
export const isIndicationLayerId = (id: string): boolean =>
  id.startsWith(FFS_INDICATION_LAYER_PREFIX)

/**
 * Builds a rect-primitive markup layer from a parsed `RolodexIndication`.
 *
 * Mirrors the shape from `useHighlightRegions.ts:82-103`:
 *   data.primitives is a Record<string, Primitive> Object literal at JSON time.
 *   `GeckoMarkupLayer.CreateFromJSON` (factory) materialises it into the typed
 *   `Map<string, GeckoMarkupPrimitive>` the viz consumes.
 *
 * Returns an array so the caller can `.flatMap` and skip indications without a
 * `selection` (yields `[]`).
 */
export function indicationToRectLayer(
  indication: RolodexIndication,
  classification: string | undefined,
  isSelected = false
): GeckoMarkupLayer[] {
  const selection = indication.selection
  if (!selection) return []

  const color = isSelected
    ? SELECTED_HIGHLIGHT_COLOR
    : ((classification && CLASSIFICATION_COLORS[classification]) ??
      UNCLASSIFIED_COLOR)

  const x_start = selection.x.start.num
  const x_end = selection.x.end.num
  const y_start = selection.y.start.num
  const y_end = selection.y.end.num
  const id = indication.id

  const geometry = {
    x_start,
    y_start,
    x_end,
    y_end,
    x_measurement_unit: selection.x.start.unit.id,
    y_measurement_unit: selection.y.start.unit.id,
    type: 'rect' as const,
  }

  const primitives: Record<string, unknown> = {
    [`${TITLE_PRIMITIVE_PREFIX}/${id}`]: {
      x: x_start + (x_end - x_start) / 2,
      y: y_start + (y_end - y_start) / 2,
      text: indication.title,
      kind: 'label',
    },
    [id]: {
      x_start,
      y_start,
      x_end,
      y_end,
      kind: 'rect',
    },
  }

  const json: RolodexEntryJson = {
    id: `${FFS_INDICATION_LAYER_PREFIX}${id}`,
    type: RolodexEntryType.MARKUP_LAYER,
    tags: indication.tags ?? {},
    data: {
      name: indication.title,
      path: ['FFS Indications'],
      color,
      category: 'highlight-region',
      geometry,
      primitives,
    },
    index: {},
    generation: 1,
    updated_at: new Date(0).toISOString(),
    access_restriction: null,
  }

  try {
    return [GeckoMarkupLayer.CreateFromJSON(json)]
  } catch (err) {
    console.warn(`Failed to construct indication markup layer ${id}:`, err)
    return []
  }
}
